import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './useAuth';
import { toast } from './use-toast';

interface UseSocketOptions {
  autoConnect?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
}

export function useSocket(options: UseSocketOptions = {}) {
  const {
    autoConnect = true,
    reconnectionAttempts = 5,
    reconnectionDelay = 1000,
  } = options;

  const { user, isAuthenticated } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user || !autoConnect) {
      return;
    }

    const socketUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3070';

    // Create socket instance
    const socket = io(socketUrl, {
      auth: {
        userId: user.id,
        organizationId: user.organization?.id,
      },
      reconnectionAttempts,
      reconnectionDelay,
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    // Connection event handlers
    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
      setIsConnected(true);
      setConnectionError(null);
      
      // Join user-specific and organization-specific rooms
      socket.emit('join_user_room', user.id);
      if (user.organization?.id) {
        socket.emit('join_organization_room', user.organization.id);
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
      
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, don't show error
        return;
      }
      
      toast({
        title: 'Connection Lost',
        description: 'Attempting to reconnect...',
        variant: 'destructive',
      });
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setConnectionError(error.message);
      setIsConnected(false);
      
      toast({
        title: 'Connection Error',
        description: 'Unable to connect to real-time updates',
        variant: 'destructive',
      });
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
      setConnectionError(null);
      
      toast({
        title: 'Reconnected',
        description: 'Real-time updates restored',
      });
    });

    socket.on('reconnect_error', (error) => {
      console.error('Socket reconnection error:', error);
      setConnectionError(error.message);
    });

    socket.on('reconnect_failed', () => {
      console.error('Socket reconnection failed');
      setConnectionError('Failed to reconnect');
      
      toast({
        title: 'Connection Failed',
        description: 'Unable to restore real-time updates. Please refresh the page.',
        variant: 'destructive',
      });
    });

    // Authentication error handler
    socket.on('auth_error', (error) => {
      console.error('Socket authentication error:', error);
      toast({
        title: 'Authentication Error',
        description: 'Real-time connection not authorized',
        variant: 'destructive',
      });
    });

    // Generic error handler
    socket.on('error', (error) => {
      console.error('Socket error:', error);
      toast({
        title: 'Socket Error',
        description: error.message || 'An error occurred with real-time updates',
        variant: 'destructive',
      });
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
    };
  }, [isAuthenticated, user, autoConnect, reconnectionAttempts, reconnectionDelay]);

  // Manual connection control
  const connect = () => {
    if (socketRef.current && !isConnected) {
      socketRef.current.connect();
    }
  };

  const disconnect = () => {
    if (socketRef.current && isConnected) {
      socketRef.current.disconnect();
    }
  };

  // Helper functions for common actions
  const joinSession = (sessionId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('join_session', sessionId);
    }
  };

  const leaveSession = (sessionId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('leave_session', sessionId);
    }
  };

  const sendSessionUpdate = (sessionId: string, update: any) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('session_update', { sessionId, ...update });
    }
  };

  return {
    socket: socketRef.current,
    isConnected,
    connectionError,
    connect,
    disconnect,
    joinSession,
    leaveSession,
    sendSessionUpdate,
  };
}